import {getAuth, signOut} from 'firebase/auth';
import {useAuthState} from 'react-firebase-hooks/auth';
import {BlockedKarelWithText} from 'course/aboutcourse/BlockedKarel';
import {useHistoryNavigate} from 'hooks/router/useHistoryNavigate';
import {useIsMobile} from 'utils/general';
import styled from 'styled-components';
import {Stack} from 'react-bootstrap';
import React from 'react';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import type {CourseId} from 'types/common';
import {useCourseId} from 'hooks/router/useUrlParams';
import {Loading} from 'components/loading/Loading';
import {reloadPage} from 'appReload';

const DESKTOP_KAREL_SCALE = 2.2;
const MOBILE_KAREL_SCALE = 1.3;
const DESKTOP_MESSAGE_SCALE_REM = 1.8;
const MOBILE_MESSAGE_SCALE_REM = 1.4;

interface ErrorPageProps {
  /**
   * The message to display on the error page. If a custom message is provided,
   * the CALLER is responsible for translating it.
   */
  msg?: string | React.ReactNode;
  /**
   * The title of the error page. If a custom title is provided,
   * the CALLER is responsible for translating it.
   */
  title?: string | React.ReactNode;
  /**
   * The course ID to navigate to if the user is not signed in. If not provided,
   * will default to the current course ID. Only necessary for local development.
   */
  courseId?: CourseId;
  /**
   * Whether to offer a Reload button in place of Sign Out / Sign In, for
   * failures a fresh page load can fix. Omitted → false.
   */
  showReload?: boolean;
}

const ErrorPageMessage = styled.p<{isMobile: boolean}>`
  font-size: ${props =>
    props.isMobile
      ? `${MOBILE_MESSAGE_SCALE_REM}rem`
      : `${DESKTOP_MESSAGE_SCALE_REM}rem`};
`;

/**
 * A component that displays an error page.
 */
export const ErrorPage = ({
  title,
  msg,
  courseId,
  showReload = false,
}: ErrorPageProps) => {
  const t = useTranslateFunction('miscUI');
  console.error('Error encountered:', msg);
  console.trace();

  const isMobile = useIsMobile();
  const currentCourseId = useCourseId();
  const effectiveCourseId = courseId ?? currentCourseId ?? 'public';

  const displayedMessage =
    typeof msg === 'string' ? (
      <ErrorPageMessage isMobile={isMobile}>{msg}</ErrorPageMessage>
    ) : (
      (msg ?? t('An unknown error occurred'))
    );

  return (
    <Stack gap={2} className="align-items-center justify-content-center vh-100">
      {/* Title / Heading */}
      {title && <h1>{title}</h1>}

      {/* Karel Graphic */}
      <div className="d-flex align-items-center">
        <BlockedKarelWithText
          scale={isMobile ? MOBILE_KAREL_SCALE : DESKTOP_KAREL_SCALE}
        />
      </div>

      {/* Message */}
      <div className="d-flex text-center mb-0 pb-0 fs-5">
        {displayedMessage}
      </div>

      {/* Actions */}
      <CurrentUserDisplay
        courseId={effectiveCourseId}
        showReload={showReload}
      />
    </Stack>
  );
};

const CurrentUserDisplay = ({
  courseId,
  showReload,
}: {
  courseId: CourseId;
  showReload: boolean;
}) => {
  const [user, loading] = useAuthState(getAuth());
  const userEmail = user?.email ?? '';
  const userDisplayName = user?.displayName ?? '';

  const t = useTranslateFunction('miscUI');
  const navigate = useHistoryNavigate();

  if (loading) {
    return <Loading />;
  }

  let innerMessage = '';
  if (userEmail) {
    innerMessage = t('You are signed in as') + ' ' + userEmail + '.';
  } else if (userDisplayName) {
    innerMessage = t('You are signed in as') + ' ' + userDisplayName + '.';
  } else {
    innerMessage = t('You are not signed in to Code in Place.') as string;
  }

  const updateLoginState = () => {
    if (!!user) {
      signOut(getAuth());
    } else {
      navigate(`/${courseId}`);
    }
  };

  return (
    <>
      <div className="fs-5">{innerMessage}</div>
      <div className="d-flex align-items-center mt-0 pt-0">
        {showReload && (
          <button
            onClick={() => reloadPage({reason: 'error-page'})}
            className="btn btn-primary mt-0 me-2"
          >
            {t('Reload')}
          </button>
        )}
        <button
          onClick={() => navigate(-1)}
          className={`btn ${showReload ? 'btn-secondary' : 'btn-primary me-2'} mt-0`}
        >
          {t('Go Back')}
        </button>
        {!showReload && (
          <button onClick={updateLoginState} className="btn btn-secondary mt-0">
            {!!user ? t('Sign Out') : t('Sign In')}
          </button>
        )}
      </div>
    </>
  );
};
